import React from 'react';
import PropTypes from 'prop-types';
import MainLogo from '../../../../assets/images/logo3PL.svg';

// ============================================
// ATOMIC COMPONENTS (Reusable Building Blocks)
// ============================================

// SectionHeader: Blue-styled section title used across invoice sections
export const SectionHeader = ({ label, marginBottom = '10px' }) => (
    <div style={{ fontSize: '11px', fontWeight: '700', color: '#0066cc', letterSpacing: '0.5px', marginBottom }}>
        {label}
    </div>
);

SectionHeader.propTypes = {
    label: PropTypes.string.isRequired,
    marginBottom: PropTypes.string,
};

// PeriodCard: Individual period or info card (PERIOD FROM, PERIOD TO, CYCLE, WAREHOUSE)
export const PeriodCard = ({ label, value }) => (
    <div style={{ padding: '15px', backgroundColor: '#f0f4ff', borderRadius: '6px', border: '1px solid #e0e8ff' }}>
        <div
            style={{
                fontSize: '10px',
                fontWeight: '700',
                color: '#0066cc',
                letterSpacing: '0.5px',
                marginBottom: '6px',
            }}
        >
            {label}
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{value}</div>
    </div>
);

PeriodCard.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
};

// ActionButton: Standardized button with optional primary styling and disabled state
export const ActionButton = ({ icon, label, onClick, variant = 'default', disabled = false }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        style={{
            padding: '8px 12px',
            border: variant === 'primary' ? 'none' : '1px solid #e0e0e0',
            borderRadius: '4px',
            backgroundColor: variant === 'primary' ? '#0066cc' : '#fff',
            color: variant === 'primary' ? '#fff' : '#333',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: variant === 'primary' ? '600' : 'normal',
            opacity: disabled ? 0.6 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
        }}
    >
        <i className={icon}></i> {label}
    </button>
);

ActionButton.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    variant: PropTypes.string,
    disabled: PropTypes.bool,
};

// SummaryRow: Flex row displaying label and monetary value (used in invoice summary)
export const SummaryRow = ({ label, value, highlight = false }) => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: highlight ? '12px 16px' : '10px 0',
            backgroundColor: highlight ? '#001f3f' : 'transparent',
            color: highlight ? '#fff' : '#666',
            borderRadius: highlight ? '4px' : '0',
            border: highlight ? 'none' : '1px solid #f0f0f0',
            fontWeight: highlight ? '600' : 'normal',
            fontSize: highlight ? '13px' : '12px',
            marginTop: highlight ? '10px' : '0',
            borderBottom: highlight ? 'none' : '1px solid #f0f0f0',
        }}
    >
        <span style={{ display: 'flex', alignItems: 'center', gap: highlight ? '6px' : '0' }}>
            {highlight && <i className="fa fa-warning"></i>}
            {label}
        </span>
        <span>${Math.abs(value).toFixed(2)}</span>
    </div>
);

SummaryRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    highlight: PropTypes.bool,
};

// LogoBox: Displays company logo/branding image
export const LogoBox = () => (
    <img
        src={MainLogo}
        alt="3PLVision Logo"
        style={{
            marginTop: '24px',
            maxWidth: '210px',
            objectFit: 'contain',
        }}
    />
);

// InvoiceHeaderInfo: Displays invoice metadata (invoice number, date, status)
export const InvoiceHeaderInfo = ({ invoiceRecord }) => (
    <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '10px' }}>TAX INVOICE</div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
            Invoice No. <span style={{ fontWeight: '600' }}>{invoiceRecord.invoiceNumber}</span>
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
            Date <span style={{ fontWeight: '600' }}>{invoiceRecord.invoiceDate}</span>
        </div>
        <div
            style={{
                display: 'inline-block',
                marginTop: '8px',
                padding: '4px 8px',
                backgroundColor: '#0066cc',
                color: '#fff',
                fontSize: '11px',
                borderRadius: '3px',
                fontWeight: '600',
            }}
        >
            {invoiceRecord.status || 'PAID'}
        </div>
    </div>
);

InvoiceHeaderInfo.propTypes = {
    invoiceRecord: PropTypes.shape({
        invoiceNumber: PropTypes.string.isRequired,
        invoiceDate: PropTypes.string.isRequired,
        status: PropTypes.string,
    }).isRequired,
};

// BillAddressSection: Individual bill section (BILL FROM or BILL TO)
export const BillAddressSection = ({ title, companyName, lines }) => (
    <div>
        <SectionHeader label={title} marginBottom="8px" />
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>{companyName}</div>
        {lines.map((line, index) => (
            <div key={`${title}-${index}`} style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                {line}
            </div>
        ))}
    </div>
);

BillAddressSection.propTypes = {
    title: PropTypes.string.isRequired,
    companyName: PropTypes.string.isRequired,
    lines: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// ============================================
// COMPOSITE COMPONENTS (Sections of Invoice)
// ============================================

// Sub-subcomponent: Action Buttons
export const InvoiceActionButtons = ({
    toBeShown = false,
    invoiceRecord,
    onDownloadPDF,
    onResendEmail,
    onWhatsApp,
    isLoading = false,
}) => (
    <div style={{ display: 'flex', gap: '10px' }}>
        {toBeShown && (
            <>
                <ActionButton
                    icon="fa fa-whatsapp"
                    label="WhatsApp"
                    onClick={() => onWhatsApp?.(invoiceRecord)}
                    variant="default"
                />
                <ActionButton
                    icon="fa fa-envelope"
                    label="Resend Email"
                    onClick={() => onResendEmail?.(invoiceRecord)}
                    variant="default"
                />
            </>
        )}
        <ActionButton
            icon="fa fa-download"
            label="Download PDF"
            onClick={() => onDownloadPDF?.(invoiceRecord)}
            variant="primary"
            disabled={isLoading}
        />
    </div>
);

InvoiceActionButtons.propTypes = {
    toBeShown: PropTypes.bool,
    invoiceRecord: PropTypes.object.isRequired,
    onDownloadPDF: PropTypes.func.isRequired,
    onResendEmail: PropTypes.func.isRequired,
    onWhatsApp: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
};

// Sub-subcomponent: Branding Section
export const InvoiceBrandingSection = ({ invoiceRecord }) => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px',
            paddingBottom: '20px',
            borderBottom: '3px solid #0066cc',
        }}
    >
        <div style={{ display: 'flex', gap: '15px' }}>
            <LogoBox />            
        </div>
        <InvoiceHeaderInfo invoiceRecord={invoiceRecord} />
    </div>
);

InvoiceBrandingSection.propTypes = {
    invoiceRecord: PropTypes.object.isRequired,
};

// Sub-subcomponent: Bill From / Bill To
export const BillFromToSection = ({ displayCompany, displaySeller, invoiceRecord }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '20px' }}>
        <BillAddressSection
            title="BILL FROM"
            companyName="3PLVision Warehouse Operations"
            lines={['Warehouse: ABE2', 'info@3plvision.com', 'Whatsapp: +1 732-277-2073']}
        />
        <BillAddressSection
            title="BILL TO"
            companyName={displayCompany}
            lines={[displaySeller ? `Seller: ${displaySeller}` : '', `Seller Code: ${invoiceRecord?.sellerCode}`].filter(
                Boolean
            )}
        />
    </div>
);

BillFromToSection.propTypes = {
    displayCompany: PropTypes.string.isRequired,
    displaySeller: PropTypes.string,
    invoiceRecord: PropTypes.shape({
        sellerCode: PropTypes.string.isRequired,
    }).isRequired,
};

// Sub-subcomponent: Period Cards
export const PeriodCardsSection = ({ invoiceRecord, hasRentItems, periodFromDate, periodToDate }) => {
    const cycleValue = hasRentItems ? 'Monthly' : 'Daily';
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '20px' }}>
            <PeriodCard label="PERIOD FROM" value={periodFromDate || '-'} />
            <PeriodCard label="PERIOD TO" value={periodToDate || '-'} />
            <PeriodCard label="CYCLE" value={cycleValue} />
        </div>
    );
};

PeriodCardsSection.propTypes = {
    invoiceRecord: PropTypes.object.isRequired,
    hasRentItems: PropTypes.bool.isRequired,
    periodFromDate: PropTypes.string,
    periodToDate: PropTypes.string,
};

// Sub-subcomponent: Service Charges Table
export const ServiceChargesTable = ({ serviceCharges }) => (
    <div style={{ marginBottom: '20px', pageBreakInside: 'avoid' }}>
        <SectionHeader label="SERVICE CHARGES" />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', pageBreakInside: 'auto' }}>
            <thead>
                <tr style={{ backgroundColor: '#001f3f', color: '#fff' }}>
                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', fontSize: '11px' }}>#</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', fontSize: '11px' }}>SERVICE</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', fontSize: '11px' }}>
                        <div>SKU</div>
                        <div>Tracking #</div>
                    </th>
                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', fontSize: '11px' }}>TXN</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600', fontSize: '11px' }}>UNIT</th>
                    <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600', fontSize: '11px' }}>QTY</th>
                    <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', fontSize: '11px' }}>RATE</th>
                    <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', fontSize: '11px' }}>ADD.</th>
                    <th style={{ padding: '10px', textAlign: 'right', fontWeight: '600', fontSize: '11px' }}>AMOUNT</th>
                </tr>
            </thead>
            <tbody>
                {serviceCharges.map((charge, index) => (
                    <tr
                        key={charge.id}
                        style={{
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9',
                        }}
                    >
                        <td style={{ padding: '10px', textAlign: 'left', color: '#666' }}>{index + 1}</td>
                        <td style={{ padding: '10px', textAlign: 'left', color: '#333', fontWeight: '500' }}>
                            {charge.service}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'left', color: '#666' }}>
                            <div style={{ color: '#333', fontWeight: '500' }}>{charge.sku || 'N/A'}</div>
                            <div>{charge.ref || 'N/A'}</div>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'left', color: '#666' }}>{charge.txn}</td>
                        <td style={{ padding: '10px', textAlign: 'left', color: '#666' }}>{charge.unit}</td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#333', fontWeight: '500' }}>
                            {charge.qty}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#666' }}>
                            {charge.rate === null ? '-' : `$${charge.rate.toFixed(2)}`}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#666' }}>${charge.add.toFixed(2)}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#333', fontWeight: '600' }}>
                            ${charge.amount.toFixed(2)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

ServiceChargesTable.propTypes = {
    serviceCharges: PropTypes.arrayOf(PropTypes.object).isRequired,
};

// Sub-subcomponent: Summary Section
export const InvoiceSummarySection = ({ subtotal, additionalCharges, taxRate, totalDue }) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <div style={{ width: '340px' }}>
            <SummaryRow label="Subtotal" value={subtotal} />
            <SummaryRow label="Additional Charges" value={additionalCharges} />
            <SummaryRow label={`Tax (${taxRate}%)`} value={(subtotal * taxRate) / 100} />
            <SummaryRow label="Total Amount - Paid From Wallet " value={totalDue} highlight={true} />
        </div>
    </div>
);

InvoiceSummarySection.propTypes = {
    subtotal: PropTypes.number.isRequired,
    additionalCharges: PropTypes.number.isRequired,
    taxRate: PropTypes.number.isRequired,
    totalDue: PropTypes.number.isRequired,
};

// Sub-subcomponent: Footer
export const InvoiceFooter = () => (
    <div
        style={{
            fontSize: '11px',
            color: '#999',
            textAlign: 'center',
            borderTop: '1px solid #f0f0f0',
            paddingTop: '15px',
            lineHeight: '1.6',
        }}
    >
        <div>This invoice was automatically generated by 3PLVision.</div>
        <div>For billing queries contact info@3plvision.com</div>
        <div style={{ marginTop: '5px' }}>© 2026 3PLVision. All rights reserved.</div>
    </div>
);

// Loading Spinner Component
export const ServiceChargesLoadingSpinner = () => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            backgroundColor: '#f9f9f9',
            borderRadius: '4px',
            border: '1px solid #e0e0e0',
        }}
    >
        <div style={{ textAlign: 'center' }}>
            <div
                style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '4px solid #e0e0e0',
                    borderTop: '4px solid #0066cc',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 12px',
                }}
            />
            <div style={{ fontSize: '14px', color: '#666', fontWeight: '500' }}>Loading service charges...</div>
        </div>
        <style>{`
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);
